import { useState, useEffect, useCallback } from 'react';

interface UseControlledStateOptions<T> {
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  initialValue?: T;
}

interface UseControlledStateReturn<T> {
  value: T;
  isControlled: boolean;
  setValue: (newValue: T) => void;
  reset: () => void;
}

/**
 * Hook genérico para manejar estado controlado y no controlado
 * @param options - Opciones de configuración
 * @returns Objeto con el valor actual, si es controlado, y funciones para manejarlo
 */
export const useControlledState = <T>(
  options: UseControlledStateOptions<T>
): UseControlledStateReturn<T> => {
  const { value, defaultValue, onChange, initialValue } = options;

  // Determinar si el componente es controlado
  const isControlled = value !== undefined;

  // Valor inicial para estado no controlado
  const initialInternalValue = defaultValue !== undefined ? defaultValue : initialValue;

  // Estado interno para componentes no controlados
  const [internalValue, setInternalValue] = useState<T | undefined>(initialInternalValue);

  // Valor actual (controlado o no controlado)
  const currentValue = isControlled ? value : internalValue;

  // Función para actualizar el valor
  const setValue = useCallback(
    (newValue: T) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange]
  );

  // Función para resetear al valor inicial
  const reset = useCallback(() => {
    const resetValue = defaultValue !== undefined ? defaultValue : initialValue;
    if (!isControlled) {
      setInternalValue(resetValue);
    }
    onChange?.(resetValue as T);
  }, [isControlled, defaultValue, initialValue, onChange]);

  // Sincronizar valor cuando cambie desde fuera (solo para controlado)
  useEffect(() => {
    if (isControlled && value !== undefined) {
      setInternalValue(value);
    }
  }, [isControlled, value]);

  return {
    value: currentValue as T,
    isControlled,
    setValue,
    reset,
  };
};

/**
 * Hook especializado para valores primitivos (string, number, boolean)
 */
export const useControlledPrimitive = <T extends string | number | boolean>(
  options: UseControlledStateOptions<T>
): UseControlledStateReturn<T> => {
  return useControlledState(options);
};

/**
 * Hook especializado para arrays
 */
export const useControlledArray = <T>(
  options: UseControlledStateOptions<T[]>
): UseControlledStateReturn<T[]> => {
  return useControlledState(options);
};

/**
 * Hook especializado para objetos
 */
export const useControlledObject = <T extends Record<string, unknown>>(
  options: UseControlledStateOptions<T>
): UseControlledStateReturn<T> => {
  return useControlledState(options);
};
